import {Component, memo} from 'react';
import DebugBoundary from 'src/public/DebugBoundary';
import useAnalytics from 'src/public/useAnalytics';
import useMetrics from 'src/public/useMetrics';
import useObservabilityConfig from 'src/public/useObservabilityConfig';
import useProject from 'src/public/useProject';
import useReports from 'src/public/useReports';

import type {ComponentType, ErrorInfo, ReactNode} from 'react';
import type {
  IAnalytics,
  IMetrics,
  IReports,
  ProjectName,
  ServiceName,
} from 'src/types';

type Props = {
  /**
   * Nodes wrapped by the error boundary and for which it will capture and
   * report errors. Ownership information is determined by the closest
   * `<ObservabilityProvider>`.
   */
  children: ReactNode;

  /**
   * Optional debugging string.
   */
  component?: string | undefined;

  /**
   * Error view to be rendered when an error is captured.
   */
  errorView: ComponentType<{error: Error; reset: () => void}>;

  /**
   * Optional callback to be called when there is an error. You DO NOT need to
   * provide one to track the error, it is automatically done.
   */
  onError?: ((error: Error, errorInfo: ErrorInfo) => void) | undefined;

  /**
   * In order to ease finding where a React error was captured and reported,
   * the ESLint rule `@sail/observability-uuid` generates a
   * unique id composed of all the error boundaries that are part of the chain,
   * and separates them by dashes.
   */
  uuid: string;
};

type InnerProps = Props & {
  analyticsTrack: IAnalytics['track'];
  metricIncrement: IMetrics['increment'];
  project: ProjectName;
  reportError: IReports['error'];
  service: ServiceName | undefined;
};

type InnerState = {
  error: Error | null;
};

class InnerErrorBoundary extends Component<InnerProps, InnerState> {
  static getDerivedStateFromError(error: Error): InnerState {
    return {error};
  }

  state: InnerState = {
    error: null,
  };

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const {
      component,
      onError,
      analyticsTrack,
      metricIncrement,
      project,
      reportError,
      service,
      uuid,
    } = this.props;

    // Suffix with SAILROOT at the end, so that the composition order is clear
    // (from the outermost to the innermost boundary).
    const fullUuid = `${uuid}-SAILROOT`;

    reportError(`ErrorBoundary error '${error.message}'`, {
      tags: {
        errorBoundaryUuid: fullUuid,
      },

      extras: {
        service,
        project,
        component,
        uuid: fullUuid,

        // Extra data in sentry is capped and component stack and js stack can be very big.
        // More info: https://develop.sentry.dev/sdk/data-handling/#variable-size.
        componentStack: errorInfo.componentStack?.trim().substring(0, 500),
        jsStack: error.stack?.trim().substring(0, 500),
      },
    });

    // For Splunk.
    analyticsTrack('frontend.observability.error_boundary', {
      service,
      project,
      component,
      uuid: fullUuid,
    });

    // For Grafana.
    metricIncrement('frontend.observability.error_boundary', {
      service,
      project,
      component,
      uuid: fullUuid,
    });

    onError?.(error, errorInfo);
  }

  reset = (): void => {
    this.setState({error: null});
  };

  render(): ReactNode {
    const {children, project} = this.props;
    const {error} = this.state;
    const {errorView: ErrorView} = this.props;

    const view = error ? (
      <ErrorView error={error} reset={this.reset} />
    ) : (
      children
    );

    return (
      <DebugBoundary category="ErrorBoundary" name={project} color="#B3093C">
        {view}
      </DebugBoundary>
    );
  }
}

/**
 * @see https://sail.stripe.me/apis/observability/ErrorBoundary
 * @see https://sail.stripe.me/apis/observability/ObservabilityProvider
 */
export default memo(function ErrorBoundary(props: Props) {
  return (
    <InnerErrorBoundary
      analyticsTrack={useAnalytics().track}
      metricIncrement={useMetrics().increment}
      project={useProject()}
      reportError={useReports().error}
      service={useObservabilityConfig()?.service}
      {...props}
    />
  );
});
